<template>
    <div class="o-articles u-w100 u-py20 u-overflowYAuto" ref="scrollContainer">
        <div class="o-articles_inner u-container u-overflowHidden">
            <div class="o-articles__articles">
                <Article v-for="article in articles" :key="article.id" :item="article" />
                <div v-if="noMoreArticles" class="no-more-articles">{{ $t('knowledgebase.noMoreArticles') }}</div>
                <div ref="trigger" class="o-articles__trigger u-mt50"></div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue';
import api from '../services/api';
import Article from '../components/molecules/Article.vue';

const articles = ref([]);
const noMoreArticles = ref(false);
const categoryTitle = ref('');
const offset = ref(0);
const number = ref(8);
const loading = ref(false);
const observer = ref(null);
const trigger = ref(null);
const scrollContainer = ref(null);

const currentCategory = ref(window.location.hash.split('/').pop());

const emit = defineEmits(['updateHeader']);

const updateAndEmitHeader = () => {
    if (currentCategory.value) {
        categoryTitle.value = currentCategory.value.charAt(0).toUpperCase() + currentCategory.value.slice(1);
        emit('updateHeader', categoryTitle.value);
        
        setTimeout(() => {
            emit('updateHeader', categoryTitle.value);
        }, 50);
    }
};

updateAndEmitHeader();

watch(currentCategory, (newValue) => {
    if (newValue) {
        updateAndEmitHeader();
    }
});

const loadMoreArticles = async () => {
    if (loading.value || noMoreArticles.value) {
        return false; 
    }

    loading.value = true;
    try {
        const response = await api.get(`/articles?category=${currentCategory.value}&offset=${offset.value}&number=${number.value}`);
        
        if (response.data.message) {
            noMoreArticles.value = true;
            return false;
        } else {
            const newArticles = response.data.map(article => ({
                id: article.id,
                image: article.image,
                title: article.title,
                date: article.date,
                categories: article.categories,
            }));
            articles.value.push(...newArticles);
            offset.value += number.value;
            return true;
        }
    } catch (error) {
        console.error('Error loading articles:', error);
        return false;
    } finally {
        loading.value = false;
    }
};

const setupObserver = () => {
    if (observer.value) {
        observer.value.disconnect();
    }

    if (!scrollContainer.value) {
        console.error("Error: scrollContainer ref is null. Cannot set up IntersectionObserver root.");
        return;
    }
    if (!trigger.value) {
        console.error("Error: trigger ref is null. Cannot observe trigger element.");
        return;
    }

    observer.value = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && !loading.value && !noMoreArticles.value) {
            loadMoreArticles();
        }
    }, {
        root: scrollContainer.value,
        rootMargin: '0px',
        threshold: 0.1
    });

    observer.value.observe(trigger.value);
};

onMounted(async () => {

    let loadedSuccessfully = await loadMoreArticles();

    if (loadedSuccessfully && !noMoreArticles.value) {
        await nextTick(); 
        await new Promise(resolve => setTimeout(resolve, 50)); 

        if (scrollContainer.value) {
            while (!noMoreArticles.value && !loading.value && scrollContainer.value.scrollHeight <= scrollContainer.value.clientHeight) {
                await nextTick(); 
                loadedSuccessfully = await loadMoreArticles(); 
                if (!loadedSuccessfully) break; 
            }
        } else {
            console.error("Scroll container not available after initial render. Cannot perform fill loop.");
        }
    }
    
    await new Promise(resolve => setTimeout(resolve, 100)); 
    await nextTick(); 
    setupObserver();
});

onUnmounted(() => {
    if (observer.value) {
        observer.value.disconnect();
    }
});
</script>